/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useState, useEffect, useRef } from "react";
import { useI18n } from "intl/useI18n";
import debounce from "lodash.debounce";
import { cn } from "tailwind-variants";
import { pluralize } from "common/utils/format/word";
import { isMobile as checkIsMobile } from "common/utils/browser";
import addUrlQueryParams from "common/utils/url";
import Loader from "common/vue/components/Loader.react.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { track } from "jiffy-analytics";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import ArtworkHistoryModalEmptyState from "./ArtworkHistoryModalEmptyState.vue";
import ArtworkHistoryGridItem from "./ArtworkHistoryGridItem.vue";
import ArtworkHistoryModalFooter from "./ArtworkHistoryModalFooter.vue";
import { isArtworkWithError } from "./utils";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";
import ArtworkHistoryArtworksToggle from "./ArtworkHistoryArtworksToggle.vue";
import ArtworkHistoryNotification from "./ArtworkHistoryNotification.vue";
import "./ArtworkHistoryModal.scss";
import { renderList } from "common/vue/render-list";

export default function ArtworkHistoryModal({
  index = 0,
  singleSelectionMode = false,
  reorderEnabled = false,
  isMade = false,
  onBuyAgain,
  ...restProps
}: any) {
  const { t } = useI18n();
  const dtfStore = useDtfStore();
  const spanglesStore = useSpanglesStore();

  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isLoadingCta, setIsLoadingCta] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [artworksType, setArtworksType] = useState("uploaded");
  // @ts-ignore TS(4111): Property 'stickerQrScanned' comes from an index si... Remove this comment to see the full error message
  const [currentType, _setCurrentType] = useState(dtfStore.stickerQrScanned ? "false" : "true");
  const [search, setSearch] = useState("");
  const [selectedArtworks, setSelectedArtworks] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [isLazyLoading, setIsLazyLoading] = useState(false);
  const [isMobileState] = useState(() => checkIsMobile());

  const modalRef = useRef(null);
  const sortOrderSelectRef = useRef(null);
  const sortOrderWatchInitialized = useRef(false);
  const currentTypeWatchInitialized = useRef(false);
  const searchWatchInitialized = useRef(false);
  const prevSearchRef = useRef(search);

  // Stable debounced search — ref always points to latest closure
  const onSearchImplRef = useRef(null);
  // @ts-ignore TS(2322): Type '() => void' is not assignable to type 'null'... Remove this comment to see the full error message
  onSearchImplRef.current = function () {
    sendArtworkHistoryEvent("previous_purchase_search_triggered", { search_query: search });
    setArtworkHistoryPagination({ page: 1 });
    clearSelection();
    loadArtworkHistory();
  };
  // @ts-ignore TS(2721): Cannot invoke an object which is possibly 'null'.
  const debouncedOnSearch = useRef(debounce(() => onSearchImplRef.current(), 600)).current;

  // Computed
  function artworkHistory() {
    // @ts-ignore TS(4111): Property 'artworkHistory' comes from an index sign... Remove this comment to see the full error message
    return dtfStore.artworkHistory;
  }
  function artworkHistoryPagination() {
    // @ts-ignore TS(4111): Property 'artworkHistoryPagination' comes from an ... Remove this comment to see the full error message
    return dtfStore.artworkHistoryPagination;
  }
  function catalogNumber() {
    // @ts-ignore TS(4111): Property 'catalogNumber' comes from an index signa... Remove this comment to see the full error message
    return dtfStore.catalogNumber;
  }
  function pdpType() {
    // @ts-ignore TS(4111): Property 'pdpType' comes from an index signature, ... Remove this comment to see the full error message
    return dtfStore.pdpType;
  }
  function sticker() {
    // @ts-ignore TS(4111): Property 'sticker' comes from an index signature, ... Remove this comment to see the full error message
    return dtfStore.sticker;
  }
  function isPuff() {
    // @ts-ignore TS(4111): Property 'isPuff' comes from an index signature, s... Remove this comment to see the full error message
    return dtfStore.isPuff;
  }
  function isSpangle() {
    // @ts-ignore TS(4111): Property 'isSpangle' comes from an index signature... Remove this comment to see the full error message
    return dtfStore.isSpangle;
  }
  function isStickerSample() {
    // @ts-ignore TS(4111): Property 'isStickerSample' comes from an index sig... Remove this comment to see the full error message
    return dtfStore.isStickerSample;
  }
  function artworks() {
    // @ts-ignore TS(4111): Property 'artworks' comes from an index signature,... Remove this comment to see the full error message
    return dtfStore.artworks;
  }
  function isGoldFoil() {
    // @ts-ignore TS(4111): Property 'isGoldFoil' comes from an index signatur... Remove this comment to see the full error message
    return dtfStore.isGoldFoil;
  }
  function puffMaxImageCount() {
    // @ts-ignore TS(4111): Property 'puffMaxImageCount' comes from an index s... Remove this comment to see the full error message
    return dtfStore.puffMaxImageCount;
  }
  function printAsIs() {
    // @ts-ignore TS(4111): Property 'printAsIs' comes from an index signature... Remove this comment to see the full error message
    return dtfStore.printAsIs;
  }
  function businessLine() {
    // @ts-ignore TS(4111): Property 'businessLine' comes from an index signat... Remove this comment to see the full error message
    return dtfStore.businessLine;
  }
  function spanglesMaxImageCount() {
    // @ts-ignore TS(4111): Property 'spanglesMaxImageCount' comes from an ind... Remove this comment to see the full error message
    return spanglesStore.spanglesMaxImageCount;
  }

  function defineOriginalWidth(ordered: any) {
    if (ordered.enhanceImage) {
      return ordered.removeBackground
        ? ordered.enhancedMetadata?.widthInch
        : ordered.withBgMetadata?.widthInch;
    }
    return ordered.originalMetadata?.widthInch;
  }

  function defineOriginalHeight(ordered: any) {
    if (ordered.enhanceImage) {
      return ordered.removeBackground
        ? ordered.enhancedMetadata?.heightInch
        : ordered.withBgMetadata?.heightInch;
    }
    return ordered.originalMetadata?.heightInch;
  }

  function getPreviewUrl(original: any, ordered: any) {
    if (isGoldFoil() && currentType === "true") return ordered.goldFoilImage;
    const imageUrl = getDownloadUrl(original, ordered);
    return addUrlQueryParams(imageUrl, { w: "150", q: "70", dpr: "2" });
  }

  function getDownloadUrl(original: any, ordered: any) {
    if (currentType === "false") {
      return original.imageUrl;
    }
    if (isSpangle() && ordered.spangledImageUrl) {
      return ordered.spangledImageUrl;
    }
    if (sticker() && ordered.cuttingUrl) {
      return ordered.cutlineUrl;
    }
    return ordered.imageUrl || original.imageUrl;
  }

  function getSpangledState(ordered: any) {
    return isSpangle() && ordered.spangled && currentType === "true";
  }

  /* eslint-disable max-lines-per-function */
  function formattedArtworks() {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return artworkHistory().map(
      ({
        id = null,
        fileName = "",
        originalMime = "",
        enhancementTier = "",
        uploadedOn = "",
        purchasedDate = "",
        ordered = {},
        original = {},
        useFootLength,
        dtfPrintId,
        pinningSessionId
      }: any) => ({
        id,
        dtfPrintId,
        name: fileName,
        uploadedOn,
        purchasedDate,
        originalMime,
        enhancementTier,
        url: ordered.imageUrl,
        previewUrl: getPreviewUrl(original, ordered),
        downloadUrl: getDownloadUrl(original, ordered),
        spangled: getSpangledState(ordered),
        spangledImageUrl: ordered?.spangledImageUrl,
        cutlineUrl: ordered?.cutlineUrl,
        originalImageUrl: original.imageUrl,
        originalMimeType: original.mimeType,
        processTimeStamp: Date.now(),
        goildFoilPreviewAttached: isGoldFoil() && currentType === "true",
        manualEditUrl: ordered.manualEditUrl || null,
        manualEditNoTrimUrl: ordered.manualEditNoTrimUrl || null,
        editImageManual: ordered.editImageManual || false,
        pinningSessionId: pinningSessionId || null,
        enhancedNoBgUrl: ordered.noBgUrl,
        enhancedWithBgUrl: ordered.withBgUrl,
        enhancementCompleted: true,
        originalDpi: ordered.dpi,
        enhancedDpi: ordered.dpi,
        sliderPreviewUrl: ordered.noBgNoTrimUrl,
        canRemoveBg: ordered.canRemoveBg || false,
        useFootLength: useFootLength || false,
        metadata: {
          ...ordered.metadata,
          dpi: ordered.dpi,
          width: ordered.widthPx,
          height: ordered.heightPx,
          widthInch: ordered.width,
          heightInch: ordered.height,
          maxSheetHeight: ordered.maxSheetHeight,
          maxSheetWidth: ordered.maxSheetWidth,
          aspectRatio: ordered?.enhanceImage
            ? (
                parseFloat(ordered?.enhancedMetadata?.width) /
                parseFloat(ordered?.enhancedMetadata?.height)
              ).toPrecision(6)
            : (
                parseFloat(ordered?.originalMetadata?.width) /
                parseFloat(ordered?.originalMetadata?.height)
              ).toPrecision(6),
          lengthFoot: ordered.lengthFoot,
          removeBackground: ordered.removeBackground
        },
        originalMetadata: ordered?.originalMetadata,
        enhancedMetadata: ordered?.enhancedMetadata,
        withBgMetadata: ordered?.withBgMetadata,
        enhanceImage: ordered?.enhanceImage,
        previewMetadata: printAsIs() ? { heightInch: ordered.height } : {},
        enhancedOriginalDpi: ordered?.enhancedOriginalDpi,
        originalWidth: defineOriginalWidth(ordered),
        originalHeight: defineOriginalHeight(ordered),
        sizeBasedDpi: ordered?.enhanceImage
          ? ordered?.enhancedOriginalDpi
          : ordered?.originalMetadata?.dpi
      })
    );
  }
  /* eslint-enable max-lines-per-function */

  function isMaxNumberOfArtworksSelected() {
    return (
      (isPuff() && selectedArtworks.length === puffMaxImageCount()) ||
      (isSpangle() && selectedArtworks.length === spanglesMaxImageCount()) ||
      (isStickerSample() && selectedArtworks.length >= 1)
    );
  }

  function selectedArtworkCountMessage() {
    return isMaxNumberOfArtworksSelected()
      ? `Maximum ${t("image", selectedArtworks.length)} can be selected.`
      : `${selectedArtworks.length} Selected`;
  }

  function buttonText() {
    if (isSpangle() && currentType === "false") {
      return "Spangle It";
    }
    if (reorderEnabled && artworksType === "purchased") {
      return "Add to Cart";
    }
    return `Select ${pluralize(selectedArtworks.length, "Design", "s", true)}`;
  }

  function withError() {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return isArtworkWithError(artworks()[0]);
  }

  function isPdp() {
    return pdpType() !== "non-pdp";
  }

  // Store action wrappers
  function setShowArtworkHistoryModal(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.setShowArtworkHistoryModal(...args);
  }

  function setArtworkHistory(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.setArtworkHistory(...args);
  }

  function setArtworkHistoryPagination(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.setArtworkHistoryPagination(...args);
  }

  function removeArtwork(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.removeArtwork(...args);
  }

  function setSpangleVariations(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return spanglesStore.setSpangleVariations(...args);
  }

  function getArtworkHistory(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.getArtworkHistory(...args);
  }

  function submitPrintReorder(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.submitPrintReorder(...args);
  }

  function buyArtworksAgain(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.buyArtworksAgain(...args);
  }

  function downloadAndTriggerArtworkUploadFlow(...args: any[]) {
    // @ts-ignore TS(2571): Object is of type 'unknown'.
    return dtfStore.downloadAndTriggerArtworkUploadFlow(...args);
  }

  function clearSelection() {
    setSelectedArtworks([]);
  }

  function clearLoading() {
    setApiLoading(false);
    setIsLazyLoading(false);
  }

  function sendArtworkHistoryEvent(event: any, data: any) {
    submitDtfEvent(event, {
      page: "pdp",
      business_line: businessLine(),
      pdp_type: pdpType(),
      catalog_number: catalogNumber(),
      ...data
    });
  }

  function showModal() {
    // @ts-ignore TS(2581): Cannot find name '$'. Do you need to install type ... Remove this comment to see the full error message
    const $modal = $(modalRef.current);
    $modal.modal("show").on("hidden.bs.modal", () => {
      // @ts-ignore TS(2554): Expected 1 arguments, but got 0.
      onClose();
      document.body.classList.remove("dtf-artwork-history-open");
    });
    document.body.classList.add("dtf-artwork-history-open");
  }

  function onClose(source: any) {
    if (source) {
      sendArtworkHistoryEvent("previous_purchase_modal_closed", { source });
    }
    setShowArtworkHistoryModal(false);
    setArtworkHistory([]);
    setArtworkHistoryPagination({ page: 1 });
    // @ts-ignore TS(2581): Cannot find name '$'. Do you need to install type ... Remove this comment to see the full error message
    $(modalRef.current).modal("hide");
  }

  async function loadArtworkHistory() {
    // TODO(vue-migration): possible stale closure — artworksType, search, sortOrder, currentType read in async context
    setApiLoading(true);
    const props = {
      artworksType,
      search,
      sortOrder,
      artworkIds: [],
      currentType,
      apiType: "list"
    };
    await getArtworkHistory(props);
    clearLoading();
  }

  async function fetchSelectedArtworks() {
    // TODO(vue-migration): possible stale closure — search, sortOrder, selectedArtworks, currentType read in async context
    setApiLoading(true);
    const props = {
      search,
      sortOrder,
      // @ts-ignore TS(2339): Property 'id' does not exist on type 'never'.
      artworkIds: selectedArtworks.map(artwork => artwork.id),
      currentType,
      apiType: "purchased"
    };
    await getArtworkHistory(props);
    clearLoading();
  }

  async function onShow() {
    showModal();
    setArtworkHistoryPagination({ perPage: isMobileState ? 20 : 36 });
    loadArtworkHistory();
  }

  // @ts-ignore TS(6133): 'selectArtworkHistory' is declared but its value i... Remove this comment to see the full error message

  async function updateArtworksType(type: any) {
    setArtworksType(type);
    setArtworkHistoryPagination({ page: 1 });
    clearSelection();
    setArtworkHistory([]);
    await loadArtworkHistory();
  }

  function checkArtworkSelection({ id, metadata }: any, artwork: any) {
    const selectedArtworkSize = `${metadata.widthInch}${metadata.heightInch}`;
    const artworkSize = `${artwork.metadata.widthInch}${artwork.metadata.heightInch}`;
    return id === artwork.id && selectedArtworkSize === artworkSize;
  }

  function isArtworkSelected(artwork: any) {
    return selectedArtworks.some(selectedArtwork =>
      checkArtworkSelection(selectedArtwork, artwork)
    );
  }

  function selectArtwork(artwork: any, idx: any) {
    if (singleSelectionMode) {
      // @ts-ignore TS(2322): Type 'any' is not assignable to type 'never'.
      setSelectedArtworks([formattedArtworks()[idx]]);
      return;
    }
    if (isArtworkSelected(artwork)) {
      setSelectedArtworks(prev =>
        prev.filter(selectedArtwork => !checkArtworkSelection(selectedArtwork, artwork))
      );
    } else {
      // @ts-ignore TS(2345): Argument of type '(prev: never[]) => any[]' is not... Remove this comment to see the full error message
      setSelectedArtworks(prev => [...prev, formattedArtworks()[idx]]);
    }
  }

  function scroll({ target }: any) {
    // @ts-ignore TS(2339): Property 'page' does not exist on type 'unknown'.
    const { page, totalPages } = artworkHistoryPagination();
    const isBottom = Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight;
    if (!isBottom || page >= totalPages) {
      return;
    }
    setIsLazyLoading(true);
    setArtworkHistoryPagination({ page: page + 1 });
    loadArtworkHistory();
    sendArtworkHistoryEvent("previous_purchase_lazy_loaded", { page_number: page + 1 });
  }

  function onToggleSearch() {
    if (isMobileState) {
      setIsSearchExpanded(prev => !prev);
    }
  }

  function toggleSelect() {
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    if (window.getComputedStyle(sortOrderSelectRef.current).display === "none") {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    }
  }

  async function buyPrintReorder() {
    setIsLoadingCta(true);
    await submitPrintReorder(selectedArtworks);
    onClose("buy_again_button");
    setIsLoadingCta(false);
  }

  async function buyAgain() {
    // TODO(vue-migration): possible stale closure — async function reads state after await
    setArtworkHistoryPagination({ page: 1 });
    await fetchSelectedArtworks();
    const newSelectedArtworks = formattedArtworks();
    // @ts-expect-error TS(2345): type mismatch with SetStateAction
    setSelectedArtworks(newSelectedArtworks);
    if (withError()) {
      // @ts-ignore TS(2571): Object is of type 'unknown'.
      removeArtwork(artworks()[0].id);
    }
    if (isSpangle()) setSpangleVariations([]);
    if (sticker()) {
      downloadAndTriggerArtworkUploadFlow(newSelectedArtworks);
    } else if (isPdp()) {
      buyArtworksAgain(newSelectedArtworks, index);
    } else if (onBuyAgain) {
      onBuyAgain(newSelectedArtworks);
    }
    sendArtworkHistoryEvent("previous_purchase_buy_again_clicked", {
      artworks_ids: newSelectedArtworks.map((artwork: any) => artwork.id),
      artworks_count: newSelectedArtworks.length
    });
    onClose("buy_again_button");
  }

  function download(url: any, name: any) {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const $a = document.createElement("a");
        $a.href = URL.createObjectURL(blob);
        $a.download = name;
        document.body.appendChild($a);
        $a.click();
        document.body.removeChild($a);
        sendArtworkHistoryEvent("previous_purchase_image_downloaded", {
          file_name: name,
          image_url: url,
          file_size: blob.size,
          file_type: blob.type
        });
      });
  }

  function onSubmit() {
    if (reorderEnabled && artworksType === "purchased") {
      buyPrintReorder();
    } else {
      buyAgain();
    }
  }

  // mounted + beforeUnmount
  useEffect(() => {
    onShow();
    return () => {
      // @ts-ignore TS(2581): Cannot find name '$'. Do you need to install type ... Remove this comment to see the full error message
      $(modalRef.current).off("hidden.bs.modal").modal("hide");
      setShowArtworkHistoryModal(false);
    };
  }, []);

  // watch: sortOrder
  useEffect(() => {
    if (!sortOrderWatchInitialized.current) {
      sortOrderWatchInitialized.current = true;
      return;
    }
    setArtworkHistoryPagination({ page: 1 });
    clearSelection();
    loadArtworkHistory();
  }, [sortOrder]);

  // watch: currentType
  useEffect(() => {
    if (!currentTypeWatchInitialized.current) {
      currentTypeWatchInitialized.current = true;
      return;
    }
    setArtworkHistoryPagination({ page: 1 });
    setArtworkHistory([]);
    clearSelection();
    loadArtworkHistory();
    track("modal_with_previous_uploads_tab_selected", { current_type: currentType });
  }, [currentType]);

  // watch: search
  useEffect(() => {
    if (!searchWatchInitialized.current) {
      searchWatchInitialized.current = true;
      prevSearchRef.current = search;
      return;
    }
    const oldValue = prevSearchRef.current;
    if (!search && oldValue) {
      setApiLoading(true);
    }
    prevSearchRef.current = search;
  }, [search]);

  return (
    <div
      {...restProps}
      className={cn("modal artwork-history scope-d5799a77", restProps.className)}
      id="artwork-history"
      ref={modalRef}
      role="dialog"
      data-testid="modal"
    >
      <div className="artwork-history__modal-box modal-dialog scope-d5799a77">
        <div
          className="artwork-history__modal-header scope-d5799a77"
          role="heading"
          aria-label="Your previous purchases"
          // @ts-ignore TS(2322): Type 'string' is not assignable to type 'number'.
          tabIndex="0"
          data-testid="artwork-history-modal-header"
        >
          <span className="artwork-history__title scope-d5799a77"> My Library </span>
          <button
            className="artwork-history__modal-button-close scope-d5799a77"
            type="button"
            onClick={() => {
              onClose("close_button");
            }}
            data-testid="artwork-history-modal-button-close"
          >
            <UseSvg className="scope-d5799a77" icon="modal-close-big" data-testid="use-svg" />
          </button>
        </div>
        <div className="artwork-history__modal-body scope-d5799a77">
          <div className="artwork-history__subheader scope-d5799a77">
            {!isSearchExpanded ? (
              <div
                className="artwork-history__dropdown scope-d5799a77"
                onClick={toggleSelect}
                data-testid="artwork-history-dropdown"
              >
                <span className="artwork-history__field-adornment scope-d5799a77">
                  <UseSvg
                    className="artwork-history__field-adornment-icon scope-d5799a77"
                    icon={`arrow-${sortOrder === "asc" ? "up" : "down"}`}
                    data-testid="artwork-history-field-adornment-icon"
                  />
                </span>
                <select
                  className="artwork-history__dropdown-select scope-d5799a77"
                  ref={sortOrderSelectRef}
                  value={sortOrder}
                  onChange={e => {
                    setSortOrder(e.target.value);
                  }}
                  data-testid="artwork-history-dropdown-select"
                >
                  <option
                    className="scope-d5799a77"
                    value="desc"
                    data-testid="artwork-history-modal-option"
                  >
                    Newest First
                  </option>
                  <option
                    className="scope-d5799a77"
                    value="asc"
                    data-testid="artwork-history-modal-option-1"
                  >
                    Oldest First
                  </option>
                </select>
              </div>
            ) : null}
            {reorderEnabled && !isSearchExpanded ? (
              <ArtworkHistoryArtworksToggle
                className="artwork-history__toggle scope-d5799a77"
                disabled={apiLoading}
                type={artworksType}
                onUpdateType={updateArtworksType}
                data-testid="artwork-history-toggle"
              />
            ) : null}
            <div
              className={cn(
                "artwork-history__input scope-d5799a77",
                isSearchExpanded && "artwork-history__input--full-width"
              )}
            >
              {isSearchExpanded || !isMobileState ? (
                <input
                  className="artwork-history__input-field scope-d5799a77"
                  value={search}
                  onInput={e => {
                    setSearch((e.target as HTMLInputElement).value);
                    // @ts-ignore TS(2554): Expected 0 arguments, but got 1.
                    debouncedOnSearch(e);
                  }}
                  placeholder="Search image names"
                  type="text"
                  data-testid="artwork-history-input-field"
                />
              ) : null}
              <span
                className="artwork-history__field-adornment scope-d5799a77"
                onClick={onToggleSearch}
                data-testid="artwork-history-field-adornment"
              >
                <UseSvg
                  className="artwork-history__field-adornment-icon scope-d5799a77"
                  icon="search"
                  data-testid="artwork-history-field-adornment-icon-1"
                />
              </span>
            </div>
            {isSearchExpanded ? (
              <button
                className="artwork-history__modal-button-close scope-d5799a77"
                type="button"
                onClick={onToggleSearch}
                data-testid="artwork-history-modal-button-close-1"
              >
                <UseSvg className="scope-d5799a77" icon="modal-close-big" data-testid="use-svg-1" />
              </button>
            ) : null}
          </div>
          {/* @ts-ignore TS(2571): Object is of type 'unknown'. */}
          {artworkHistory().length < 1 ? (
            <ArtworkHistoryModalEmptyState
              className="scope-d5799a77"
              isUploadedState={reorderEnabled && artworksType === "uploaded"}
              isLoading={apiLoading}
              isMade={isMade}
              data-testid="artwork-history-modal-empty-state"
            />
          ) : (
            <div
              className="artwork-history__artworks-wrapper scope-d5799a77"
              onScroll={scroll}
              data-testid="artwork-history-artworks-wrapper"
            >
              {reorderEnabled && artworksType === "purchased" ? (
                <ArtworkHistoryNotification
                  className="artwork-history__artworks-notification scope-d5799a77"
                  data-testid="artwork-history-artworks-notification"
                >
                  {
                    "You won't be charged for design services when reordering a design at the same size."
                  }
                </ArtworkHistoryNotification>
              ) : null}
              <div className="artwork-history__grid scope-d5799a77">
                {renderList(formattedArtworks(), (artwork, idx) => (
                  <ArtworkHistoryGridItem
                    className="scope-d5799a77"
                    key={idx}
                    artwork={artwork}
                    isSelected={isArtworkSelected(artwork)}
                    index={idx}
                    isShownTier={reorderEnabled && artworksType === "purchased"}
                    isShownCheckbox={!isMaxNumberOfArtworksSelected() || isArtworkSelected(artwork)}
                    onDownloadArtwork={download}
                    onSelectLibrary={selectArtwork}
                    data-testid="artwork-history-grid-item"
                  />
                ))}
                <div
                  className={cn(
                    "artwork-history__loading scope-d5799a77",
                    isLazyLoading && "artwork-history__loading--visible"
                  )}
                >
                  <Loader
                    className={cn("scope-d5799a77", "artwork-history__loading-icon")}
                    data-testid="artwork-history-loading-icon"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {selectedArtworks.length > 0 ? (
          <ArtworkHistoryModalFooter
            className="scope-d5799a77"
            count={selectedArtworkCountMessage()}
            ctaText={buttonText()}
            isLoadingCta={isLoadingCta}
            onClose={onClose}
            onCtaAction={onSubmit}
            data-testid="artwork-history-modal-footer"
          />
        ) : null}
      </div>
    </div>
  );
}
